import { Component, Input, OnInit, SimpleChanges } from '@angular/core';
import { EnodeService } from 'src/app/services/enode/enode.service';
import {
  EnodeVehicle,
  Intervention,
  SmartChargingPlanPayload,
  SmartChargingStatus,
  UserLocation
} from 'src/app/services/enode/enode.types';
import { InterventionModalComponent } from '../intervention-modal/intervention-modal.component';
import { ModalController } from '@ionic/angular';
import { differenceInHours, intlFormatDistance } from 'date-fns';
import { hasChanged } from 'src/app/utils/change-utils';
import { Observable } from 'rxjs';
import { AddLocationModalComponent } from '../add-location-modal/add-location-modal.component';

@Component({
  selector: 'app-vehicle',
  templateUrl: './vehicle.component.html',
  styleUrls: ['./vehicle.component.scss']
})
export class VehicleComponent implements OnInit {
  @Input() vehicle: EnodeVehicle;
  @Input() chargeActionMessages: { [key: string]: string };
  @Input() locations: UserLocation[];

  smartChargingSocket: Observable<SmartChargingPlanPayload>;
  smartChargeStatus: SmartChargingStatus;
  interventions: Intervention[] = [];
  modalPage: HTMLIonModalElement;
  locationName: string;
  connectionState: string;
  locationState: string;
  showEventLog: boolean = false;
  isCapable: boolean = false;

  constructor(
    private _enodeService: EnodeService,
    public modalCtrl: ModalController
  ) {}

  ngOnInit() {
    this.isCapable = this.vehicle.capabilities.smartCharging.isCapable;
    this.getSmartChargeStatus();
    if (this.hasInterventions()) {
      this.getInterventions();
    }
    this.setAddress();
  }

  ionViewWillEnter() {
    this.smartChargingSocket = this._enodeService.getSmartChargingStream();

    this.smartChargingSocket.subscribe({
      next: (data) => {
        if (data.payload) {
          if (data.payload.vehicleId === this.vehicle.id) {
            this.smartChargeStatus = data.payload;
            this.connectionState = this.getConnectionState();
          }
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.vehicle?.firstChange && hasChanged([changes.vehicle])) {
      if (this.hasInterventions()) {
        this.getInterventions();
      }
      this.locationState = this.getLocationState();
      this.getSmartChargeStatus();
    }
    if (!changes.locations?.firstChange && hasChanged([changes.locations])) {
      this.locationState = this.getLocationState();
    }
  }

  getSmartChargeStatus() {
    this._enodeService.getSmartChargingStatus(this.vehicle.id).subscribe({
      next: (res) => {
        this.smartChargeStatus = res;
        this.connectionState = this.getConnectionState();
      }
    });
  }

  async showModal() {
    this.modalPage = await this.modalCtrl.create({
      component: InterventionModalComponent,
      cssClass: 'intervention-modal-content',
      componentProps: {
        vehicle: this.vehicle,
        interventions: this.interventions
      },
      backdropDismiss: true
    });
    return await this.modalPage.present();
  }

  getLastSeen() {
    return (
      'Sist sett ' +
      intlFormatDistance(new Date(this.vehicle.lastSeen), new Date(), {
        locale: 'nb-NO'
      })
    );
  }

  getLocationState() {
    if (!this.vehicle.isReachable) {
      return `Sist sett ${intlFormatDistance(
        new Date(this.vehicle.lastSeen),
        new Date(),
        {
          locale: 'nb-NO'
        }
      )} `;
    }

    if (
      this.vehicle.isReachable &&
      this.vehicle.location.latitude &&
      !this.vehicle.locationId
    ) {
      return this.locationName
        ? `Nær ${this.locationName}`
        : 'Bilen er ikke på kjent ladelokasjon';
    }
    if (this.vehicle.locationId) {
      const locationName = this.locations?.find(
        (l) => l.id === this.vehicle.locationId
      )?.name;
      return `På ${locationName ? locationName : 'ukjent ladelokasjon'}`;
    }
    return 'Kan ikke hente bilens lokasjon';
  }

  getConnectionState() {
    if (!this.vehicle.isReachable) {
      return 'Får ikke kontakt med bil';
    }
    if (!this.vehicle.chargeState.isPluggedIn) {
      return 'Lader ikke tilkoblet';
    }
    if (!this.vehicle.chargeState.isCharging) {
      return 'Lading pauset';
    }
    if (this.vehicle.chargeState.isFullyCharged) {
      return 'Fulladet';
    }
    if (
      this.smartChargeStatus?.state === 'PLAN:EXECUTING:STOPPED' ||
      this.smartChargeStatus?.state === 'PLAN:EXECUTING:STOPPED:AWAITING_PRICES'
    ) {
      return 'Lading pauset';
    }
    if (this.smartChargeStatus?.state === 'PLAN:EXECUTING:STOPPING') {
      return 'Pauser lading';
    }
    if (this.smartChargeStatus?.state === 'PLAN:EXECUTING:STARTING') {
      return 'Starter lading';
    }
    if (this.vehicle.chargeState.isCharging) {
      return 'Lader';
    }
    // TODO: Smartladingsstatuser
  }

  getInterventions() {
    const interventionIds = new Set([
      ...this.vehicle.capabilities.chargeState.interventionIds,
      ...this.vehicle.capabilities.information.interventionIds,
      ...this.vehicle.capabilities.location.interventionIds,
      ...this.vehicle.capabilities.odometer.interventionIds,
      ...this.vehicle.capabilities.smartCharging.interventionIds,
      ...this.vehicle.capabilities.startCharging.interventionIds,
      ...this.vehicle.capabilities.stopCharging.interventionIds
    ]);
    this.interventions = [];
    interventionIds.forEach((id) => {
      this._enodeService.getIntervention(id).subscribe({
        next: (intervention) => {
          this.interventions.push(intervention);
        }
      });
    });
  }

  hasInterventions() {
    return (
      this.vehicle.capabilities.chargeState.interventionIds.length > 0 ||
      this.vehicle.capabilities.information.interventionIds.length > 0 ||
      this.vehicle.capabilities.location.interventionIds.length > 0 ||
      this.vehicle.capabilities.odometer.interventionIds.length > 0 ||
      this.vehicle.capabilities.smartCharging.interventionIds.length > 0 ||
      this.vehicle.capabilities.startCharging.interventionIds.length > 0 ||
      this.vehicle.capabilities.stopCharging.interventionIds.length > 0
    );
  }

  startChargeVehicle() {
    this._enodeService.startCharging(this.vehicle.id).subscribe({
      next: (res) => {
        if (res.state === 'PENDING') {
          this.chargeActionMessages[this.vehicle.id] =
            'Lading starter... Vennligst vent';
        }
        if (res.failureReason?.title) {
          this.chargeActionMessages[this.vehicle.id] =
            'Kunne ikke starte lading: ' + res.failureReason?.title;
        }
      }
    });
  }

  stopChargeVehicle() {
    this._enodeService.stopCharging(this.vehicle.id).subscribe({
      next: (res) => {
        if (res.state === 'PENDING') {
          this.chargeActionMessages[this.vehicle.id] =
            'Stopper lading... Vennligst vent';
        }
        if (res.failureReason?.title) {
          this.chargeActionMessages[this.vehicle.id] =
            'Kunne ikke stoppe lading: ' + res.failureReason?.title;
        }
      }
    });
  }

  async getAddress(lat: number, lng: number): Promise<string> {
    if (lat && lng) {
      const geocoder = new google.maps.Geocoder();
      const res = await geocoder.geocode({
        location: { lat: lat, lng: lng }
      });
      const place = res.results.find((result) => {
        if (result.types.includes('street_address')) {
          return result;
        } else if (result.types.includes('establishment')) {
          return result;
        }
      });
      return place?.formatted_address;
    }
  }

  async setAddress() {
    if (this.vehicle?.location) {
      const address = await this.getAddress(
        this.vehicle.location.latitude,
        this.vehicle.location.longitude
      );
      const parsedAddress = address?.split(',');
      this.locationName = parsedAddress ? parsedAddress[0] : undefined;
      this.locationState = this.getLocationState();
    }
  }

  async showLocationModal() {
    this.modalPage = await this.modalCtrl.create({
      component: AddLocationModalComponent,
      cssClass: 'add-location-modal-content',
      backdropDismiss: true
    });
    return await this.modalPage.present();
  }
}
